import queryString from "query-string";

import { type AssignedUserExtraData, BTSelectItem } from "types/apiResponse/apiResponse";
import { POMappingEntityTypes } from "types/enum";

import { stringToArray } from "utilities/string/string";

import { RelatedItemType } from "commonComponents/entity/relatedItem/RelatedItem.types";

import {
    AssignedUserPoIdTitleMapping,
    EntityAssignment,
    type IPoMappingCreationRequest,
    type IPoMappingLineItemAssignmentFormValues,
    LineItemAssignment,
    LineItemPoAssignedUserMapping,
    type PoForMappingExtraData,
    type PoMappingDetailConfigurationFormValues,
    PoMappingDetailPurchaseOrder,
    type PoMappingEntity,
    PoMappingLineItem,
    type PoMappingResponse,
} from "entity/purchaseOrder/PoMapping/PoMappingInternal/PoMappingInternal.api.types";
import {
    PurchaseOrderLineItem,
    PurchaseOrderRelatedItem,
} from "entity/purchaseOrder/PurchaseOrder/PurchaseOrder.api.types";

/**
 * Used by PoMappingLineItemAssignment for converting PoMappingResponse entities
 * into values that are compatible with the line item table's datasource.
 * @param entities
 * @param isToExistingPo
 */
export const responseEntitiesToAssignmentEntities = function (
    entities: PoMappingEntity[],
    isToExistingPo: boolean,
    performingUsers: BTSelectItem<AssignedUserExtraData>[] | null,
) {
    const data: (EntityAssignment | LineItemAssignment)[] = [];
    entities.forEach((entity: PoMappingEntity) => {
        data.push(new EntityAssignment(entity));
        entity.lineItems.forEach((lineItem: PoMappingLineItem) => {
            data.push(new LineItemAssignment(lineItem, entity, isToExistingPo, performingUsers));
        });
    });
    return data;
};

/**
 * The form values between PoMappingLineItemAssignment and PoMappingDetailConfiguration are different shapes.
 * This function takes the form values that output from PoMappingLineItemAssignment and transforms the values
 * into a shape that PoMappingDetailConfiguration will accept for its form values.
 * @param data The data field from an object that implements IPoMappingLineItemAssignmentFormValues.
 * @param entities  The mapping entities from PoMappingResponse.
 * @param createSeparatePo  If true, one PO will be created for each line item.
 *                          Otherwise, one PO will be created for each unique assigned user.
 */
export function transformLineItemAssignmentsToDetailConfigPOs(
    data: (EntityAssignment | LineItemAssignment)[],
    createSeparatePo: boolean,
): PoMappingDetailPurchaseOrder[] {
    if (createSeparatePo) {
        return transformLineItemAssignmentsToSeparateDetailConfigPOs(data);
    }
    return transformLineItemAssignmentsToGroupedDetailConfigPOs(data);
}

/** Create one PO for each selected line item. */
function transformLineItemAssignmentsToSeparateDetailConfigPOs(
    data: (EntityAssignment | LineItemAssignment)[],
): PoMappingDetailPurchaseOrder[] {
    const { entityAssignments, lineItemAssignments } = separateEntityAndLineItemAssignments(data);
    /* Create Separate POs: Map each LineItemAssignment object to a new purchase order */
    return lineItemAssignments
        .filter((lineItemAssignments) => lineItemAssignments.isChecked)
        .map((lineItemAssignment) => {
            /* Grab parent entity information */
            let entityAssignment: EntityAssignment;

            /* If the entity is a Selection Choice or Bid Request, we need to find parent information
            off of the grandparent id (Selection, Bid Package), not the direct parent information */
            if (lineItemAssignment.grandparentEntityId) {
                entityAssignment = entityAssignments.find(
                    (e) => lineItemAssignment.grandparentEntityId === e.id,
                )!;
            } else {
                /* Otherwise we can grab the parent entity assignment through the direct parent id */
                entityAssignment = entityAssignments.find(
                    (e) => lineItemAssignment.parentEntityId === e.id,
                )!;
            }

            // Only include the line item description if the user checked the box to do so,
            // otherwise use an empty string for the line item description
            const lineItemDescription = lineItemAssignment.includeDescription
                ? lineItemAssignment.description
                : "";
            const poMappingLineItem = new PoMappingLineItem({
                ...lineItemAssignment,
                lineItemId: lineItemAssignment.id,
                description: lineItemDescription,
            });
            poMappingLineItem.parentEntityTitle = getLineItemParentEntityTitle(
                poMappingLineItem,
                entityAssignments,
            );
            poMappingLineItem.parentEntityDescription =
                getLineItemParentEntityDescription(poMappingLineItem, entityAssignments) || "";

            return new PoMappingDetailPurchaseOrder({
                // When creating separate POs for each line item, never assume the PO title
                poTitle: "",
                poNumber: "",
                entityId: entityAssignment.id,
                displayTitle: entityAssignment.displayTitle,
                description: entityAssignment.description || "",
                performingUserTitle: lineItemAssignment.performingUserName,
                performingUserId: lineItemAssignment.assignToId,
                /* Creating separate POs, this array will only have one item */
                lineItems: [poMappingLineItem],
            });
        });
}

/** Create one PO for each assigned user.
 * The line items assigned to this PO will be all line items assigned to that user.  */
function transformLineItemAssignmentsToGroupedDetailConfigPOs(
    data: (EntityAssignment | LineItemAssignment)[],
) {
    const { lineItemAssignments, entityAssignments } = separateEntityAndLineItemAssignments(data);
    const distinctAssignedUsers = lineItemAssignments
        /* map line items to an object containing the assigned user id and display name */
        .map((lineItem) => ({
            assignToId: lineItem.assignToId,
            performingUserName: lineItem.performingUserName,
        }))
        /* filter the list by distinct assigned user ids */
        .filter(
            (value, index, array) =>
                array.indexOf(array.find((x) => x.assignToId === value.assignToId)!) === index,
        );

    /* Sort the assigned users alphabetically by name */
    distinctAssignedUsers.sort((a, b) => {
        if (a.performingUserName < b.performingUserName) {
            return -1;
        } else if (a.performingUserName > b.performingUserName) {
            return 1;
        } else {
            return 0;
        }
    });

    const detailConfigPos: PoMappingDetailPurchaseOrder[] = [];

    distinctAssignedUsers.forEach((assignedUser) => {
        const assignToId = assignedUser.assignToId;
        const lineItemsAssignedToUser = lineItemAssignments.filter(
            (lineItem) => lineItem.isChecked && lineItem.assignToId === assignToId,
        );

        if (lineItemsAssignedToUser.length === 0) {
            return;
        }

        /* Determine the po title and description */
        let poTitle = lineItemsAssignedToUser[0].title;
        let poDescription = "";

        // get entity assignments associated with line items
        const associatedEntityAssignments = lineItemsAssignedToUser
            .map((lineItem) => {
                /* if there's grandparentId, we're working with a bid request or selection choice */
                /* ensure that the lineItem.parentEntityId lines up with the selection choice id or bid request id */
                /* AND ensure that the lineItem.grandParentEntityId lines up with the selection id or the bid package id */
                if (lineItem.grandparentEntityId) {
                    return entityAssignments.find(
                        (entity) =>
                            entity.childId === lineItem.parentEntityId &&
                            entity.id === lineItem.grandparentEntityId,
                    )!;
                }
                /* otherwise if there's no grandparentId, we're working with a change order or estimate */
                /* we can get away with only comparing the lineItem.parentEntityId to the change order or estimate id */
                return entityAssignments.find((entity) => entity.id === lineItem.parentEntityId)!;
            })
            // remove duplicates
            .filter((value, index, array) => array.indexOf(value) === index);

        /* If all line items share the same parent... */
        if (associatedEntityAssignments.length === 1) {
            // grab just the first line item, we only need it to find out what kind of entity we are working with
            const lineItem = lineItemsAssignedToUser[0];
            // reassigning the only associated entity to a variable 'parentEntity' for easier reading
            const parentEntity = associatedEntityAssignments[0];

            /* If there is a grandparent id, we're looking at either a selection or bid */
            if (lineItem.grandparentEntityId) {
                /* If there is a parent entity title, we're looking at a selection choice */
                if (lineItem.parentEntityTitle) {
                    /* We can set the PO title and description to the selection choice's */
                    poTitle = lineItem.parentEntityTitle;
                    poDescription = lineItem.parentEntityDescription;
                } else {
                    /* Otherwise we're looking at a bid request */
                    /* And we can set the po title to the bid package title */
                    poTitle = lineItem.grandparentEntityTitle;
                }
            } else {
                /* Otherwise we're looking at an estimate or change order */
                poTitle = parentEntity.title;
                poDescription = parentEntity.description;
            }
        }

        const detailConfigPo = new PoMappingDetailPurchaseOrder({
            poTitle: poTitle,
            poNumber: "",
            performingUserTitle: lineItemsAssignedToUser[0].performingUserName,
            performingUserId: assignedUser.assignToId,
            description: poDescription,
            /* Put all line items assigned to this user onto this PO */
            lineItems: lineItemsAssignedToUser.map((lineItemAssignment) => {
                // Only include the line item description if the user checked the box to do so,
                // otherwise use an empty string for the line item description
                const lineItemDescription = lineItemAssignment.includeDescription
                    ? lineItemAssignment.description
                    : "";
                const poMappingLineItem = new PoMappingLineItem({
                    ...lineItemAssignment,
                    lineItemId: lineItemAssignment.id,
                    description: lineItemDescription,
                });
                poMappingLineItem.parentEntityTitle = getLineItemParentEntityTitle(
                    poMappingLineItem,
                    entityAssignments,
                );
                poMappingLineItem.parentEntityDescription =
                    getLineItemParentEntityDescription(poMappingLineItem, entityAssignments) || "";
                return poMappingLineItem;
            }),
        });
        detailConfigPos.push(detailConfigPo);
    });
    return detailConfigPos;
}

/**
 * Get a string to be assigned to PoMappingLineItem.parentEntityTitle. This is used for selections and
 * change orders to display an additional column on the line item table, showing the parent entity
 * that each line item came from.
 * @param poMappingLineItem
 * @param entityAssignments
 */
function getLineItemParentEntityTitle(
    poMappingLineItem: PoMappingLineItem,
    entityAssignments: EntityAssignment[],
): string {
    // If there is not a grandparent id, we are looking at either a change order or estimate
    if (!poMappingLineItem.grandparentEntityId) {
        // For change orders, return the displayTitle field of the parent entity, which is the change order name including the CO number
        // This will also end up returning the displayTitle for bid packages as well, but this is not an issue as the line item
        // table for bids does not include the additional column that selections and change orders have.
        return entityAssignments.find(
            (entityAssignment) => entityAssignment.id === poMappingLineItem.parentEntityId,
        )!.displayTitle;
    }
    // Otherwise just return whatever parentEntityTitle was already there, no changes
    else {
        return poMappingLineItem.parentEntityTitle;
    }
}

/**
 * From the line item use the line item's parent entity id to lookup the description
 * of the source entity that the line item came from.
 * @param poMappingLineItem
 * @param entityAssignments
 */
function getLineItemParentEntityDescription(
    poMappingLineItem: PoMappingLineItem,
    entityAssignments: EntityAssignment[],
): string {
    // If there is a grandparentEntityId (bids, selections)
    if (poMappingLineItem.grandparentEntityId) {
        /* If there is a parent entity title, we're looking at a selection choice */
        if (poMappingLineItem.parentEntityTitle) {
            /* We can set the poDescription to the selection choice description */
            return poMappingLineItem.parentEntityDescription;
        }
        // then we need to get the parent entity using that grandparentEntityId and return the description
        return entityAssignments.find(
            (entityAssignment) => entityAssignment.id === poMappingLineItem.grandparentEntityId,
        )!.description;
    }
    // Otherwise if there is only a parentEntityId (change orders, estimates)
    else {
        // do a lookup for the parent entity using the parentEntityId and return the description
        return entityAssignments.find(
            (entityAssignment) => entityAssignment.id === poMappingLineItem.parentEntityId,
        )!.description;
    }
}

/**
 * Create a mapping request to map line items to existing POs. When mapping to existing POs,
 * there is no need for the PoMappingDetailConfiguration step, so this function allows us
 * to skip that step, create a request, and get to the PoMappingCompletionList screen.
 * @param formValues Form values from PoMappingLineItemAssignment.
 * @param entities The entities[] from a PoMappingResponse
 * @param entityType The entity type being mapped from.
 * @param proposed For estimates.
 * @param updatePendingUpdates For linked cost codes.
 */
export function transformLineItemAssignmentsToCreationRequestForExistingPOs(
    formValues: IPoMappingLineItemAssignmentFormValues,
    entities: PoMappingEntity[],
    jobId: number,
    entityType: POMappingEntityTypes,
    relatedChangeOrder: number | null,
    proposed: boolean | undefined,
    updatePendingUpdates: boolean,
): IPoMappingCreationRequest {
    return {
        varianceCodeId: formValues.varianceCode,
        createSeparatePOsForEachLineItem: formValues.createSeparatePo,
        proposed: proposed !== undefined && proposed,
        entityType: entityType,
        relatedChangeOrder: relatedChangeOrder,
        updatePendingUpdates: updatePendingUpdates,
        jobId: jobId,
        descriptions: {},
        mappingItems: convertLineItemAssignmentsToMappingItems(formValues.data, entities),
        mappingIdsAndTitles: [],
        carrySpecsForward: formValues.carrySpecsForward,
    };
}
/**
 * Both requests (create new POs or map to existing POs) require a mappingItems array to be populated.
 * The mappingItems array is the same between both workflows. This function will populate all keys on
 * each LineItemPoAssignedUserMapping item even if only some of them are needed for mapping to an existing
 * PO. If a key is unused by a workflow it will be ignored anyway, so there's no issue in populating that key.
 * @param data The data set from IPoMappingLineAssignmentFormValues.
 */
function convertLineItemAssignmentsToMappingItems(
    data: (EntityAssignment | LineItemAssignment)[],
    entities: PoMappingEntity[],
): LineItemPoAssignedUserMapping[] {
    const { lineItemAssignments } = separateEntityAndLineItemAssignments(data);
    return lineItemAssignments
        .filter((lineItem) => lineItem.isChecked)
        .map((lineItem) => {
            // Get the parent entity in order to set the titleForMapping key
            let parentEntity: PoMappingEntity;
            if (lineItem.grandparentEntityId) {
                parentEntity = entities.find(
                    (entity) => entity.id === lineItem.grandparentEntityId,
                )!;
            } else {
                parentEntity = entities.find((entity) => entity.id === lineItem.parentEntityId)!;
            }

            return new LineItemPoAssignedUserMapping({
                builderCost: lineItem.builderCost,
                quantity: lineItem.quantity,
                unitType: lineItem.unitType,
                unitCost: lineItem.unitCost,
                description: lineItem.description,
                lineDescription: lineItem.description,
                includeDescriptions: lineItem.includeDescription,
                includeFilesAndPhotos: lineItem.includeFiles,
                lineItemId: lineItem.id,
                lineItemParentId: lineItem.parentEntityId,
                lineItemGrandParentId: lineItem.grandparentEntityId,
                lineItemParentDescription: lineItem.parentEntityDescription,
                lineItemGrandParentDisplayTitle: lineItem.grandparentEntityDisplayTitle,
                costCodeTitle: lineItem.costCodeTitle,
                titleForMapping: parentEntity.title,
                title: lineItem.title,
                internalNotes: lineItem.internalNotes,
                purchaseOrderId: lineItem.assignToId,
                assignedUserId: lineItem.assignToId,
                assignToId: lineItem.assignToId,
                entityType: lineItem.entityType,
                costTypes: lineItem.costTypes,
                breakLinkFromCatalog: lineItem.breakLinkFromCatalog,
            });
        });
}

/**
 * Convert form values from PoMappingDetailConfiguration into a PO mapping creation request.
 * @param formValues Form values from the PoMappingDetailConfiguration component.
 * @param jobId A jobsite id.
 * @param entityType  The entity type being mapped from.
 * @param proposed For estimates.
 * @param updatePendingUpdates Used for determining whether the PO should use updated cost code values.
 * @param createSeparatePo Determine whether a PO should be made for each line item.
 */
export function transformDetailConfigurationFormValuesToCreationRequestForNewPOs(
    formValues: PoMappingDetailConfigurationFormValues,
    jobId: number,
    entityType: POMappingEntityTypes,
    relatedChangeOrder: number | null,
    proposed: boolean | undefined,
    createSeparatePo: boolean,
    carrySpecsForward: boolean,
): IPoMappingCreationRequest {
    return {
        varianceCodeId: formValues.varianceCode,
        createSeparatePOsForEachLineItem: createSeparatePo,
        proposed: proposed !== undefined && proposed,
        entityType: entityType,
        relatedChangeOrder: relatedChangeOrder,
        updatePendingUpdates: formValues.shouldUpdatePendingUpdates,
        jobId: jobId,
        mappingItems: convertDetailConfigurationFormValuesToMappingItems(formValues),
        mappingIdsAndTitles: convertDetailConfigurationFormValuesToMappingIdsAndTitles(
            formValues,
            createSeparatePo,
        ),
        descriptions: convertDetailConfigurationFormValuesToDescriptionsDictionary(formValues),
        carrySpecsForward: carrySpecsForward,
    };
}

/**
 * Create the list of mappingItems. The size of this list should be equal to the numeer of line items being mapped.
 * @param formValues Form values from the PoMappingDetailConfiguration component.
 */
function convertDetailConfigurationFormValuesToMappingItems(
    formValues: PoMappingDetailConfigurationFormValues,
): LineItemPoAssignedUserMapping[] {
    const mappingItems: LineItemPoAssignedUserMapping[] = [];
    formValues.purchaseOrders.forEach((po) =>
        po.lineItems.forEach((lineItem) => {
            const parentEntityDescription = lineItem.parentEntityDescription;
            const mappingItem = new LineItemPoAssignedUserMapping({
                ...lineItem,
                lineItemParentId: lineItem.parentEntityId,
                lineItemGrandParentId: lineItem.grandparentEntityId,
                lineItemGrandParentDisplayTitle: lineItem.grandparentEntityDisplayTitle,
                lineItemParentTitle: lineItem.parentEntityTitle,
                lineItemParentDescription: parentEntityDescription,
                includeDescriptions: lineItem.includeDescription,
                includeFilesAndPhotos: lineItem.includeFiles,
                description: parentEntityDescription,
                lineDescription: lineItem.description,
                titleForMapping: po.poTitle,
                assignedUserId: po.performingUserId,
            });
            mappingItems.push(mappingItem);
        }),
    );
    return mappingItems;
}

/**
 * Create the array for mappingIdsAndTitles.
 * The size of this list should be equal to the number of POs being created.
 * @param formValues Form values from the PoMappingDetailConfiguration component.
 * @param createSeparatePo Determine whether a PO should be made for each line item.
 */
function convertDetailConfigurationFormValuesToMappingIdsAndTitles(
    formValues: PoMappingDetailConfigurationFormValues,
    createSeparatePo: boolean,
): AssignedUserPoIdTitleMapping[] {
    // If separate POs should be created for each line item
    if (createSeparatePo) {
        // one entry in mappingIdsAndTitles should be made for each line item
        const mappingIdsAndTitles: AssignedUserPoIdTitleMapping[] = [];
        formValues.purchaseOrders.forEach((po) =>
            po.lineItems.forEach((lineItem) => {
                const mappingId = new AssignedUserPoIdTitleMapping();
                mappingId.lineItemId = lineItem.lineItemId;
                mappingId.assignedUserId = po.performingUserId;
                mappingId.title = po.poTitle;
                mappingId.poId = po.poNumber;
                if (po.estimatedCompletion) {
                    mappingId.estimatedCompletionDate = po.estimatedCompletion.toISOString();
                }
                mappingIdsAndTitles.push(mappingId);
            }),
        );
        return mappingIdsAndTitles;
    }
    // Otherwise group the line items by the assigned user id
    return formValues.purchaseOrders.map((po) => {
        const mappingId = new AssignedUserPoIdTitleMapping();
        /** Set the assignedUserId: the service will use this to scan through another list,
         * mappingItems, and match to other items by the assignedUserId.
         * The assignment of mappingId.lineItemId is then intentionally omitted. */
        mappingId.assignedUserId = po.performingUserId;
        mappingId.title = po.poTitle;
        mappingId.poId = po.poNumber;
        if (po.estimatedCompletion) {
            mappingId.estimatedCompletionDate = po.estimatedCompletion.toISOString();
        }
        return mappingId;
    });
}

/**
 * Create a dictionary where the keys are entity or line item ids and the values are descriptions.
 * @param formValues Form values from the PoMappingDetailConfiguration component.
 */
function convertDetailConfigurationFormValuesToDescriptionsDictionary(
    formValues: PoMappingDetailConfigurationFormValues,
): Record<number, string> {
    const descriptions: Record<number, string> = {};
    formValues.purchaseOrders.forEach((po) => {
        // Map the line item ids to the line item descriptions
        po.lineItems.forEach((lineItem) => {
            descriptions[lineItem.lineItemId] = lineItem.description;
            descriptions[lineItem.parentEntityId] = lineItem.parentEntityDescription;
        });
    });
    return descriptions;
}

/**
 * Separate a mixed list of EntityAssignments and LineItemAssignments into two different lists.
 * @param data A list of EntityAssignments and LineItemAssignments
 */
export function separateEntityAndLineItemAssignments(
    data: (EntityAssignment | LineItemAssignment)[],
): { entityAssignments: EntityAssignment[]; lineItemAssignments: LineItemAssignment[] } {
    return data.reduce(
        (
            previousValue: {
                entityAssignments: EntityAssignment[];
                lineItemAssignments: LineItemAssignment[];
            },
            currentValue: EntityAssignment | LineItemAssignment,
        ) => {
            if (currentValue instanceof EntityAssignment) {
                previousValue.entityAssignments.push(currentValue);
            } else {
                previousValue.lineItemAssignments.push(currentValue);
            }
            return previousValue;
        },
        { entityAssignments: [], lineItemAssignments: [] },
    );
}

/**
 * Return a rowKey string value used for antd tables.
 * @param row
 */
export function generateRowKeyForTableSelection(row: EntityAssignment | LineItemAssignment) {
    /**
     * In most cases this will just be the item id.
     * However, with multiple choice selections, it's possible that multiple entity assignments
     * exist from the same selection, therefore having the same id.
     * To distinguish these entries, append the selection choice id to the selection id.
     */
    if (row instanceof EntityAssignment && row.childId && row.childId > 0) {
        return `${row.id}-${row.childId}`;
    }
    if (row instanceof LineItemAssignment && row.parentEntityId && row.parentEntityId > 0) {
        return `${row.id}-${row.parentEntityId}`;
    }
    return row.id.toString();
}

/**
 * Decide if an item matches to an entity by comparing its id values.
 * @param item The item to match to an entity.
 * @param entityId What you want item.grandparentEntityId to be equal to for bid requests,
 *                 or what you want item.parentEntityId to be equal to for change orders and estimates.
 * @param childEntityId What you want item.parentEntityId to be equal to for selection choices.
 */
export function isMatchingToEntity(
    item: LineItemAssignment | EntityAssignment,
    entityId: number,
    childEntityId?: number,
) {
    if (item instanceof LineItemAssignment) {
        // If this lineItem is coming from a selection choice and has a selection as its grandparent
        if (childEntityId) {
            // then compare the item against the selection choice id
            return item.parentEntityId === childEntityId;
        }

        // If there is a grandparent id (bid requests)
        return item.grandparentEntityId > 0
            ? // match the entityId to the grandparent entity id
              item.grandparentEntityId === entityId
            : // otherwise just compare against the parent entity id (COs, estimates)
              item.parentEntityId === entityId;
    }
    return false;
}

/** LineItemAssignment onChange handler for checking an entity item in the table */
export function entityOnChange(
    data: (EntityAssignment | LineItemAssignment)[],
    selected: boolean,
    newItemIndex: number,
    previouslyImportedCOs?: number[],
) {
    // Handle associated line items
    const associatedLineItems = data.filter((row) =>
        isMatchingToEntity(
            row,
            data[newItemIndex].id,
            (data[newItemIndex] as EntityAssignment).childId,
        ),
    );

    // Filter to get only non-disabled line items
    const nonDisabledLineItems = associatedLineItems.filter((lineItem) => {
        const isDisabled =
            lineItem instanceof LineItemAssignment &&
            previouslyImportedCOs &&
            previouslyImportedCOs.includes(lineItem.id);
        return !isDisabled;
    });

    // Only process if there are non-disabled items
    if (nonDisabledLineItems.length > 0) {
        // Update non-disabled line items
        nonDisabledLineItems.forEach((lineItem) => {
            lineItem.isChecked = selected;
        });
    }
}

/** onChange handler for checking a line item in the table */
export function lineItemOnChange(
    data: (EntityAssignment | LineItemAssignment)[],
    newItemIndex: number,
    previouslyImportedCOs?: number[],
) {
    const entity = data.find((item) =>
        isMatchingToEntity(data[newItemIndex], item.id, (item as EntityAssignment).childId),
    ) as EntityAssignment;
    const associatedLineItems = data.filter((x) =>
        isMatchingToEntity(x, entity.id, entity.childId),
    );

    // Filter out disabled items when checking if all items are checked
    const nonDisabledLineItems = associatedLineItems.filter((lineItem) => {
        const isDisabled =
            lineItem instanceof LineItemAssignment &&
            previouslyImportedCOs &&
            previouslyImportedCOs.includes(lineItem.id);
        return !isDisabled;
    });

    if (
        nonDisabledLineItems.length ===
            nonDisabledLineItems.filter((lineItem) => lineItem.isChecked).length &&
        nonDisabledLineItems.length > 0
    ) {
        entity.isChecked = true;
    } else if (entity.isChecked) {
        entity.isChecked = false;
    }
}
/**
 * Returns true if there is at least one existing PO that has been assigned to more than 1 sub, returns false otherwise.
 * @param checkedLineItems
 */
export function isPoAssignedToMultipleSubs(checkedLineItems: LineItemAssignment[]) {
    const map = new Map<number, number>();
    for (const li of checkedLineItems) {
        const subId = map.get(li.assignToId);
        if (subId && li.subId !== subId) {
            return true;
        }
        map.set(li.assignToId, li.subId);
    }
    return false;
}

/** If the entity type is bids (subId is populated), then it will find purchase orders that are associated
 * to that same sub. Otherwise we will always take the list with no subId.
 */
export function getRelevantPurchaseOrderList(
    row: LineItemAssignment,
    poMapping: PoMappingResponse,
) {
    const relevantEntity = poMapping.entities.find((x) => isMatchingToEntity(row, x.id, x.childId));
    let poList;
    if (relevantEntity && relevantEntity.subId > 0) {
        const relevantPoList = poMapping.purchaseOrders.find(
            (x) => x.assignedToSubId === relevantEntity.subId,
        );
        if (relevantPoList) {
            return relevantPoList.poGroupedList;
        } else {
            // if there aren't any POs assigned to the sub, we should only show the default list
            poList = poMapping.purchaseOrders.filter((x) => x.isDefault)[0].poGroupedList;
        }
    } else {
        poList = poMapping.purchaseOrders.filter((x) => x.assignedToSubId === null)[0]
            .poGroupedList;
    }

    /**  below code for if there is only going to be one None Available option, then send an ungrouped flat list to BTSelect
     * so that it doesn't show an auto-generated group
     */
    if (poList[0].children![0].title.toString() === "-- None Available --") {
        const list = [
            new BTSelectItem<PoForMappingExtraData>({ id: -1, title: "-- None Available --" }),
        ];
        return list;
    }
    return poList;
}

export const parsePurchaseOrderMappingQueryStringParams = (search: string) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(search, { parseBooleans: true });
    const ids = qsValues.ids ? stringToArray(qsValues.ids) : [];
    const jobId = parseInt(qsValues.jobId || 0);
    const toExistingPo = qsValues.toExistingPo || false;
    const isProposed = qsValues.isProposed || false;

    return {
        ids,
        jobId,
        toExistingPo,
        isProposed,
    };
};

/**
 * Converts POMappingEntityTypes to RelatedItemType.
 * Used when creating related items from PO mapping entities.
 * @param entityType The POMappingEntityTypes value to convert
 * @returns The corresponding RelatedItemType value
 */
function convertPOMappingEntityTypeToRelatedItemType(
    entityType: POMappingEntityTypes,
): RelatedItemType {
    switch (entityType) {
        case POMappingEntityTypes.ChangeOrders:
        case POMappingEntityTypes.ChangeOrderLineItems:
            return RelatedItemType.ChangeOrder;
        case POMappingEntityTypes.Bids:
        case POMappingEntityTypes.BidLineItems:
            return RelatedItemType.Bid;
        case POMappingEntityTypes.Estimates:
            return RelatedItemType.Estimate;
        case POMappingEntityTypes.Selections:
            return RelatedItemType.Selection;
        case POMappingEntityTypes.SelectionChoices:
        case POMappingEntityTypes.SelectionChoiceLineItems:
            return RelatedItemType.SelectionOption;
        default:
            return RelatedItemType.None;
    }
}

/**
 * Converts checked line item assignments to PurchaseOrderLineItem format for remapping.
 * Used when mapping FROM a Purchase Order TO Change Orders.
 * @param data The data set from IPoMappingLineAssignmentFormValues
 */
export function convertLineItemAssignmentsToPurchaseOrderLineItems(
    data: (EntityAssignment | LineItemAssignment)[],
    varianceCode: number,
    newPurchaseOrderLineItemId: number,
): PurchaseOrderLineItem[] {
    const { lineItemAssignments } = separateEntityAndLineItemAssignments(data);

    return lineItemAssignments
        .filter((lineItem) => lineItem.isChecked)
        .map(
            (lineItem, index) =>
                new PurchaseOrderLineItem({
                    id: newPurchaseOrderLineItemId - index,
                    title: lineItem.title,
                    description: lineItem.description,
                    quantity: lineItem.quantity,
                    unitType: lineItem.unitType,
                    unitCost: lineItem.unitCost,
                    costCodeTitle: lineItem.costCodeTitle,
                    internalNotes: lineItem.internalNotes,
                    vendorProductId: lineItem.vendorProductId,
                    costTypes: lineItem.costTypes,
                    costCodeId: lineItem.costCodeId,
                    varianceCode: varianceCode > 0 ? varianceCode : 0,
                    relatedChangeOrderLineItemId: lineItem.id,
                    breakLinkFromCatalog: lineItem.breakLinkFromCatalog,
                    relatedItems: [
                        new PurchaseOrderRelatedItem({
                            id: lineItem.parentEntityId,
                            title: lineItem.parentEntityTitle,
                            type: convertPOMappingEntityTypeToRelatedItemType(lineItem.entityType),
                        }),
                    ],
                }),
        );
}

/**
 * Deduped count of specs carrying onto a PO (union across its line items' eligible specs).
 * @param po The PO whose line items to total.
 * @param specExternalIdsByLineItemId Per line item id, the external ids of specs eligible to carry forward.
 */
export function getLinkedSpecCountForPo(
    po: PoMappingDetailPurchaseOrder,
    specExternalIdsByLineItemId: Record<number, string[]>,
): number {
    const specExternalIds = new Set<string>();
    po.lineItems.forEach((lineItem) => {
        (specExternalIdsByLineItemId[lineItem.lineItemId] ?? []).forEach((id) =>
            specExternalIds.add(id),
        );
    });
    return specExternalIds.size;
}
